import { useState } from 'react';
import {
  Color, DISCOVERY_NO, DISCOVERY_YES, Size, dealDiscoveryAnswerPath, dealDiscoveryEntriesPath, dealDiscoveryEntryRemovePath, dealDiscoverySaveAsTemplatePath, dealDiscoveryTemplateRemovePath, dealDiscoveryTemplatesPath, dealDiscoveryUploadPath,
  discoveryChoicePicks, discoveryChoiceText, discoveryHeadLine, discoveryRangeText, discoveryText, entryHookOf, entryKeyOf, entryLine, groupAddLabel, isDiscoveryOther, libraryOffered, sectionHeld, sectionHeldLine, sectionLine, spotPhotoKind, t,
  type DiscoveryCardAnswer, type DiscoveryEntryView, type DiscoveryRemoveChoice, type DiscoveryGroupView, type DiscoveryQuestionView, type DiscoverySectionView, type DiscoverySpotView,
} from '@bms2/shared';
import { FoldChevron, useFoldMemory } from '../../../components/composites/FoldChevron';
import { DISCOVERY_FOLD_KEY_PREFIX } from '../../../shell/devicePreference';
import { Button, Checkbox, RadioGroup, SegmentedTab, Select, TextInput, Textarea } from '../../../components/controls';
import { Icon } from '../../../components/Icon';
import { FormFrame } from '../../../components/forms/FormFrame';
import { Modal } from '../../../components/modals/Modal';
import { post } from '../api';
import { RecordUploadModal } from '../RecordUploadModal';
import { AskClaude } from './AskClaude';
import { CardDrawnContext, DiscoverySpot, useCardDrawn } from './DiscoverySpot';
import styles from './DiscoveryCard.module.css';

export interface DiscoveryCardProps {
  /** 🔒 The card's one shape, which names the record and the stage it is bound to (decision 5450, M1b). */
  card: DiscoveryCardAnswer;
  apiBaseUrl?: string;
  onChanged: () => void;
  onError: (message: string) => void;
  onNotice?: (message: string) => void;
}

/**
 * 🔒 The Discovery card (piece 19; decision 5450, row T2750 batch 1): one
 * universal piece bound to a record and a stage by its props, so it moves to
 * another record kind without a rewrite. Its head reads the totals; Add Template
 * with the round plus opens the library sheet (M2a); each template added is one
 * section, its questions as fields writing their tagged notes and its upload
 * points as tiles that open the upload window with the spot already set (M3a);
 * and Save as Template writes a new template from the record's sections (M4c).
 * 🔒 Ask Claude sends the Deal's pack without its Budget pair and lists the open
 * questions that come back, each with an answer box (decision 5530).
 * 🔨 A viewer who does not write reads the answers and the tiles and changes
 * nothing.
 */
export function DiscoveryCard({ card, apiBaseUrl = '', onChanged, onError, onNotice }: DiscoveryCardProps) {
  const drawn = useCardDrawn();
  const [choosing, setChoosing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState<DiscoverySpotView | null>(null);
  const [removing, setRemoving] = useState<{ section: DiscoverySectionView; entry: DiscoveryEntryView } | null>(null);
  const [removingSection, setRemovingSection] = useState<DiscoverySectionView | null>(null);
  const offered = libraryOffered(card.library, card.sections);
  // 🔒 T2771: a note on an upload point is written as an answer is, tagged to the point.
  const note = async (spot: DiscoverySpotView, value: string): Promise<boolean> => {
    if (!spot.noteKey) return false;
    const outcome = await post(dealDiscoveryAnswerPath(card.recordId), { noteKey: spot.noteKey, value }, apiBaseUrl);
    if (!outcome.ok) {
      onError(outcome.message);
      return false;
    }
    onChanged();
    return true;
  };
  // 🔒 Decision 5550: Add and a List's name appends its next item, and a Group taken off is added back.
  const addEntry = async (section: DiscoverySectionView, group: DiscoveryGroupView) => {
    // 🔒 Decision 6110: a block under a List item is named by its entry key, which carries the item's number.
    const outcome = await post(dealDiscoveryEntriesPath(card.recordId), { templateId: section.templateId, groupKey: entryKeyOf(group) }, apiBaseUrl);
    if (!outcome.ok) return onError(outcome.message);
    onChanged();
  };
  const removeEntry = async (section: DiscoverySectionView, entry: DiscoveryEntryView) => {
    const outcome = await post(dealDiscoveryEntryRemovePath(card.recordId), { templateId: section.templateId, groupKey: entryKeyOf(entry), number: entry.number }, apiBaseUrl);
    if (!outcome.ok) return onError(outcome.message);
    setRemoving(null);
    onChanged();
  };
  // 🔒 Decision 5551: a section taken off, its answers and files kept plain or removed, as the person picked.
  const removeSection = async (section: DiscoverySectionView, answersAndFiles: DiscoveryRemoveChoice) => {
    const outcome = await post(dealDiscoveryTemplateRemovePath(card.recordId), { templateId: section.templateId, answersAndFiles }, apiBaseUrl);
    if (!outcome.ok) return onError(outcome.message);
    setRemovingSection(null);
    onChanged();
  };

  const add = async (templateId: string) => {
    const outcome = await post(dealDiscoveryTemplatesPath(card.recordId), { templateId }, apiBaseUrl);
    if (!outcome.ok) return onError(outcome.message);
    setChoosing(false);
    onChanged();
  };
  const answer = async (question: DiscoveryQuestionView, value: string): Promise<boolean> => {
    const outcome = await post(dealDiscoveryAnswerPath(card.recordId), { noteKey: question.noteKey, value }, apiBaseUrl);
    if (!outcome.ok) {
      onError(outcome.message);
      return false;
    }
    onChanged();
    return true;
  };

  return (
    <div className={styles.card} data-discovery-card={card.stage} data-discovery-record={card.recordType}>
      <p className={styles.head} data-discovery-head="true">{discoveryHeadLine(card.sections)}</p>
      {card.sections.length === 0 ? <p className={styles.hint}>{t('discovery.empty')}</p> : null}
      {/* 🔒 T2922: one signal for the whole card, so every tile's pictures are asked together once the card has drawn. */}
      <CardDrawnContext.Provider value={drawn}>
      {card.sections.map((section) => (
        <DiscoverySection key={section.templateId} section={section} writes={card.writes} onAnswer={answer} onUpload={setUploading} onNote={note} apiBaseUrl={apiBaseUrl} onChanged={onChanged} onError={onError} onAddEntry={(group) => void addEntry(section, group)} onRemoveEntry={(entry) => setRemoving({ section, entry })} onRemove={() => setRemovingSection(section)} />
      ))}
      </CardDrawnContext.Provider>
      {/* 🔒 Decision 5530: Ask Claude and the questions it wrote onto the Deal, for a writer and, read only, for a reader. */}
      <AskClaude dealId={card.recordId} questions={card.askClaude ?? []} writes={card.writes} asksClaude={card.asksClaude === true} report={card.report ?? null} apiBaseUrl={apiBaseUrl} onChanged={onChanged} onError={onError} {...(onNotice ? { onNotice } : {})} />
      {card.writes ? (
        <div className={styles.acts}>
          <button type="button" className={styles.addAct} onClick={() => setChoosing(true)} data-discovery-add-template="true">
            <span className={styles.addRound} aria-hidden="true"><Icon meaning="add" size="inline" /></span>
            <span>{t('discovery.addTemplate').replace(/^\+\s*/, '')}</span>
          </button>
          {card.savesTemplate && card.sections.length > 0 ? (
            <Button size={Size.Small} color={Color.Inactive} text={[t('discovery.saveAsTemplate')]} actions={[() => setSaving(true)]} />
          ) : null}
        </div>
      ) : null}
      {/* 🔨 Mounted only while open, so each opening starts on the library as it stands (piece 13). */}
      {choosing ? (
        <Modal open title={t('discovery.sheetTitle')} subtitle={t('discovery.sheetHint')} onCancel={() => setChoosing(false)} actions={[{ kind: 'close', label: t('modal.cancel'), onAct: () => setChoosing(false) }]} dataModal="discovery-library">
          {offered.length === 0 ? <p className={styles.hint}>{t('discovery.sheetEmpty')}</p> : null}
          <ul className={styles.library}>
            {offered.map((entry) => (
              <li key={entry.id}>
                <button type="button" className={styles.libraryRow} onClick={() => void add(entry.id)} data-discovery-library={entry.key}>
                  <span className={styles.libraryName}>{entry.name}</span>
                  {entry.description ? <span className={styles.hint}>{entry.description}</span> : null}
                </button>
              </li>
            ))}
          </ul>
        </Modal>
      ) : null}
      {/* 🔒 Decision 5550: an item's Remove confirms first, naming the item; mounted only while open. */}
      {removing ? (
        <Modal
          open
          title={discoveryText('discovery.removeEntryTitle', { name: removing.entry.heading })}
          onCancel={() => setRemoving(null)}
          actions={[{ kind: 'primary', label: t('discovery.removeEntry'), onAct: () => removeEntry(removing.section, removing.entry) }, { kind: 'cancel', label: t('modal.cancel'), onAct: () => setRemoving(null) }]}
          dataModal="discovery-remove-entry"
        >
          <p className={styles.hint}>{t('discovery.removeEntryKeeps')}</p>
        </Modal>
      ) : null}
      {/* 🔒 Decision 5551: a section's Remove names what it holds and asks Keep or Remove for it; holding nothing, it asks no choice. */}
      {removingSection ? <RemoveSectionWindow section={removingSection} onCancel={() => setRemovingSection(null)} onRemove={(choice) => removeSection(removingSection, choice)} /> : null}
      {saving ? <SaveAsTemplateWindow card={card} apiBaseUrl={apiBaseUrl} onClose={() => setSaving(false)} onSaved={() => { setSaving(false); onNotice?.(t('discovery.savedAsTemplate')); onChanged(); }} /> : null}
      {/* 🔨 Keyed to the spot it uploads for, so a second tile never uploads against the first one's draft; a reader is drawn none. */}
      {/* 🔒 Decision 6090: the window reports each file as it lands and closes only through its own Done or Stop, so every file picked is filed under the spot; closing it on the report dropped the rest of the queue. */}
      {/* 🔒 Decision 6000 (T2766): a photo point opens the window with its kind preset, the point's default kind or its tag. */}
      {card.writes ? (
      <RecordUploadModal
        key={uploading ? `discovery-${uploading.spot}` : 'discovery-closed'}
        open={uploading !== null}
        recordType={card.recordType}
        recordId={card.recordId}
        apiBaseUrl={apiBaseUrl}
        {...(uploading?.kind === 'Photo' ? { photoKinds: [spotPhotoKind(uploading)], presetPhotoKind: spotPhotoKind(uploading), act: 'photo' as const } : {})}
        onUploaded={async (file) => {
          if (!uploading) return;
          // 🔒 The stamp files the upload under its spot straight after it lands (decision 5450).
          const stamped = await post(dealDiscoveryUploadPath(card.recordId), { fileId: file.id, spot: uploading.spot }, apiBaseUrl);
          if (!stamped.ok) throw new Error(stamped.message);
        }}
        onCancel={() => setUploading(null)}
        onSaved={onChanged}
      />
      ) : null}
    </div>
  );
}

interface SectionActs {
  writes: boolean;
  onAnswer: (question: DiscoveryQuestionView, value: string) => Promise<boolean>;
  onUpload: (spot: DiscoverySpotView) => void;
  /** 🔒 T2771: the point's own note, the filled tile's pictures and their notes. */
  onNote: (spot: DiscoverySpotView, value: string) => Promise<boolean>;
  apiBaseUrl: string;
  onChanged: () => void;
  onError: (message: string) => void;
}

function DiscoverySection({ section, onAddEntry, onRemoveEntry, onRemove, ...acts }: SectionActs & { section: DiscoverySectionView; onAddEntry: (group: DiscoveryGroupView) => void; onRemoveEntry: (entry: DiscoveryEntryView) => void; onRemove: () => void }) {
  const { writes } = acts;
  // 🔒 Decision 6211: a section folds to its heading, which keeps its answered-of-total count; the choice is per device.
  const folds = useFoldMemory(DISCOVERY_FOLD_KEY_PREFIX);
  const folded = folds.isFolded(section.templateKey);
  return (
    <section className={styles.section} data-discovery-section={section.templateKey} aria-label={section.name} data-discovery-folded={folded ? 'true' : undefined}>
      <header className={styles.sectionHead}>
        <span className={styles.foldName}>
          <FoldChevron folded={folded} onToggle={() => folds.toggle(section.templateKey)} name={section.name} />
          <span className={styles.sectionName}>{section.name}</span>
        </span>
        <span className={styles.entryActs}>
          <span className={styles.hint} data-discovery-section-counts={section.templateKey}>{sectionLine(section)}</span>
          {writes ? <span data-discovery-remove-section={section.templateKey}><Button size={Size.Small} color={Color.Inactive} text={[t('discovery.removeEntry')]} actions={[onRemove]} /></span> : null}
        </span>
      </header>
      {!folded && (section.questions.length > 0 || section.spots.length > 0) ? <ItemBody row={section.templateKey} questions={section.questions} spots={section.spots} {...acts} /> : null}
      {/* 🔒 Decision 5550: each Group as one framed block, each List item numbered with its own Remove, and the Add a List or a Group taken off offers. */}
      {(folded ? [] : section.groups ?? []).map((group) => (
        <DiscoveryBlock key={group.key} templateKey={section.templateKey} group={group} nested={false} onAddEntry={onAddEntry} onRemoveEntry={onRemoveEntry} {...acts} />
      ))}
    </section>
  );
}

/**
 * 🔒 One Group or List on a section (decision 5550): each item framed under its heading with its own count line and
 * Remove, then the Add the List or a Group taken off offers. 🔒 Decision 6110: the blocks standing in an item are
 * drawn inside it, indented one spacing step, each with its own counts, Remove and Add.
 */
function DiscoveryBlock({ templateKey, group, nested, onAddEntry, onRemoveEntry, ...acts }: SectionActs & { templateKey: string; group: DiscoveryGroupView; nested: boolean; onAddEntry: (group: DiscoveryGroupView) => void; onRemoveEntry: (entry: DiscoveryEntryView) => void }) {
  const { writes } = acts;
  const addLabel = groupAddLabel(group);
  return (
    <div className={nested ? `${styles.group} ${styles.nested}` : styles.group} data-discovery-group={`${templateKey}/${entryKeyOf(group)}`} data-discovery-group-kind={group.kind} data-discovery-nested={nested ? 'true' : undefined}>
      {group.entries.map((entry) => (
        <DiscoveryEntry key={entry.number ?? 0} templateKey={templateKey} entry={entry} onAddEntry={onAddEntry} onRemoveEntry={onRemoveEntry} {...acts} />
      ))}
      {writes && addLabel ? (
        <button type="button" className={styles.addAct} onClick={() => onAddEntry(group)} data-discovery-add-entry={`${templateKey}/${entryKeyOf(group)}`}>
          <span className={styles.addRound} aria-hidden="true"><Icon meaning="add" size="inline" /></span>
          <span>{addLabel}</span>
        </button>
      ) : null}
    </div>
  );
}

/** 🔒 Decision 6211: one Group or List item folds to its heading, which keeps its count line and its Remove. */
function DiscoveryEntry({ templateKey, entry, onAddEntry, onRemoveEntry, ...acts }: SectionActs & { templateKey: string; entry: DiscoveryEntryView; onAddEntry: (group: DiscoveryGroupView) => void; onRemoveEntry: (entry: DiscoveryEntryView) => void }) {
  const { writes } = acts;
  const hook = `${templateKey}/${entryHookOf(entry)}`;
  const folds = useFoldMemory(DISCOVERY_FOLD_KEY_PREFIX);
  const folded = folds.isFolded(hook);
  return (
    <div className={styles.entry} data-discovery-entry={hook} data-discovery-folded={folded ? 'true' : undefined}>
      <header className={styles.sectionHead}>
        <span className={styles.foldName}>
          <FoldChevron folded={folded} onToggle={() => folds.toggle(hook)} name={entry.heading} />
          <span className={styles.entryName}>{entry.heading}</span>
        </span>
        <span className={styles.entryActs}>
          <span className={styles.hint}>{entryLine(entry)}</span>
          {writes ? <Button size={Size.Small} color={Color.Inactive} text={[t('discovery.removeEntry')]} actions={[() => onRemoveEntry(entry)]} /> : null}
        </span>
      </header>
      {!folded && (entry.questions.length > 0 || entry.spots.length > 0) ? <ItemBody row={hook} questions={entry.questions} spots={entry.spots} {...acts} /> : null}
      {(folded ? [] : entry.groups ?? []).map((inner) => (
        <DiscoveryBlock key={inner.key} templateKey={templateKey} group={inner} nested onAddEntry={onAddEntry} onRemoveEntry={onRemoveEntry} {...acts} />
      ))}
    </div>
  );
}

/**
 * 🔒 One row (row T2772, the owner's answer D5a): the template's own, a Group's or a List item's, its questions in
 * the left column and its own upload points in the right from laptop width, stacked questions first below it. A row
 * with no question keeps its tiles in the right column, so every row of a section lines up.
 */
function ItemBody({ row, questions, spots, writes, onAnswer, onUpload, onNote, apiBaseUrl, onChanged, onError }: SectionActs & { row: string; questions: readonly DiscoveryQuestionView[]; spots: readonly DiscoverySpotView[] }) {
  return (
    <div className={styles.sectionBody} data-discovery-row={row}>
      {questions.length > 0 ? (
        <div className={styles.questions} data-discovery-column="questions">
          {questions.map((question) => (
            // Keyed to the answer, so a saved answer starts the field afresh from what the server holds.
            <QuestionField key={`${question.noteKey}:${question.answer ?? ''}`} question={question} writes={writes} onAnswer={onAnswer} />
          ))}
        </div>
      ) : null}
      {spots.length > 0 ? (
        <div className={styles.spots} data-discovery-column="spots">
          {spots.map((spot) => (
            // Keyed to the point's note, so a saved note starts the box afresh from what the server holds.
            <DiscoverySpot key={`${spot.spot}:${spot.note ?? ''}`} spot={spot} writes={writes} apiBaseUrl={apiBaseUrl} onUpload={onUpload} onNote={onNote} onChanged={onChanged} onError={onError} />
          ))}
        </div>
      ) : null}
    </div>
  );
}

/**
 * 🔒 A question as a field of its kind (M3a): a box writes on leaving it, a choice writes on picking it. A Many
 * question draws a check box per option, and an option named Other opens a box under it (decision 5550).
 */
function QuestionField({ question, writes, onAnswer }: { question: DiscoveryQuestionView; writes: boolean; onAnswer: (question: DiscoveryQuestionView, value: string) => Promise<boolean> }) {
  const [draft, setDraft] = useState(question.answer ?? '');
  const commit = (value: string) => {
    if (value.trim() === '' || value.trim() === (question.answer ?? '').trim()) return;
    void onAnswer(question, value.trim());
  };
  const name = `discovery-${question.noteKey}`;
  if (!writes) {
    return (
      <div className={styles.readQuestion} data-discovery-question={question.noteKey}>
        <span className={styles.questionLabel}>{question.label}</span>
        <span className={question.answer ? styles.answer : styles.hint}>{question.answer ?? t('discovery.notAnswered')}</span>
      </div>
    );
  }
  if (question.kind === 'Choice') return <ChoiceField question={question} name={name} commit={commit} />;
  const options = question.kind === 'YesNo' ? [DISCOVERY_YES, DISCOVERY_NO] : question.options;
  const range = question.kind === 'Number' ? discoveryRangeText(question.rangeFrom, question.rangeTo) : '';
  return (
    <div className={styles.question} data-discovery-question={question.noteKey} data-discovery-question-kind={question.kind}>
      {question.kind === 'YesNo' ? (
        // 🔒 Row T2840 (decision 5921): Yes or No as the two-segment switch; an unanswered question has neither segment chosen.
        <div className={styles.switchQuestion} data-discovery-answer={question.noteKey} data-discovery-answered={draft === '' ? 'false' : 'true'}>
          <span className={styles.questionLabel}>{question.label}</span>
          <SegmentedTab
            name={name}
            legend={question.label}
            value={draft}
            onChange={(value) => { setDraft(value); commit(value); }}
            segments={options.map((option) => ({ value: option, label: option === DISCOVERY_YES ? t('discovery.yes') : t('discovery.no') }))}
          />
          {draft === '' ? <span className={styles.hint}>{t('discovery.notAnswered')}</span> : null}
        </div>
      ) : question.kind === 'Number' ? (
        <>
          <TextInput size={Size.Medium} label={question.label} value={draft} onChange={setDraft} onBlur={() => commit(draft)} placeholder={t('discovery.answerPlaceholder')} name={name} />
          {range ? <span className={styles.hint} data-discovery-range={question.noteKey}>{discoveryText('discovery.rangeHint', { range })}</span> : null}
        </>
      ) : (
        <Textarea size={Size.Large} label={question.label} value={draft} onChange={setDraft} onBlur={() => commit(draft)} placeholder={t('discovery.answerPlaceholder')} rows={2} name={name} />
      )}
    </div>
  );
}

/** 🔒 Decision 5550: a Choice of One as a choice and of Many as check boxes, never chips, each with Other's box under it once picked. */
function ChoiceField({ question, name, commit }: { question: DiscoveryQuestionView; name: string; commit: (value: string) => void }) {
  const read = discoveryChoicePicks(question.options, question.answer);
  const [picks, setPicks] = useState<string[]>(read.picks);
  const [other, setOther] = useState(read.other);
  const otherOption = question.options.find(isDiscoveryOther);
  const otherPicked = otherOption !== undefined && picks.includes(otherOption);
  const write = (next: readonly string[], words: string) => {
    if (next.length > 0) commit(discoveryChoiceText(question.options, next, words));
  };
  const otherBox = otherPicked ? (
    <TextInput size={Size.Large} label={t('discovery.otherLabel')} value={other} onChange={setOther} onBlur={() => write(picks, other)} placeholder={t('discovery.otherPlaceholder')} name={`${name}-other`} />
  ) : null;
  if (question.allowsMany) {
    return (
      <fieldset className={styles.manyChoice} data-discovery-question={question.noteKey} data-discovery-question-kind="Choice" data-discovery-many="true">
        <legend className={styles.questionLabel}>{question.label}</legend>
        {question.options.map((option) => (
          <Checkbox
            key={option}
            label={option}
            checked={picks.includes(option)}
            name={`${name}-${option}`}
            onChange={(checked) => {
              const next = checked ? [...picks, option] : picks.filter((pick) => pick !== option);
              setPicks(next);
              write(next, other);
            }}
          />
        ))}
        {otherBox}
      </fieldset>
    );
  }
  return (
    <div className={styles.question} data-discovery-question={question.noteKey} data-discovery-question-kind="Choice">
      <Select
        size={Size.Large}
        label={question.label}
        value={picks[0] ?? ''}
        onChange={(value) => { setPicks([value]); write([value], isDiscoveryOther(value) ? other : ''); }}
        placeholder={t('discovery.notAnswered')}
        options={question.options.map((option) => ({ value: option, label: option }))}
        dataAttribute={{ 'data-discovery-answer': question.noteKey }}
      />
      {otherBox}
    </div>
  );
}

/**
 * 🔒 Decision 5551 (T2773, the owner's note on D6): the section's Remove confirm. It names the answers and files the
 * section holds and asks Keep or Remove for them; the template comes off either way. Holding nothing, it asks no
 * choice.
 */
function RemoveSectionWindow({ section, onCancel, onRemove }: { section: DiscoverySectionView; onCancel: () => void; onRemove: (choice: DiscoveryRemoveChoice) => Promise<void> }) {
  const held = sectionHeld(section);
  const empty = held.answers === 0 && held.files === 0;
  // 🔨 Nothing is picked for the person: the template comes off only once Keep or Remove is chosen.
  const [choice, setChoice] = useState<DiscoveryRemoveChoice | null>(null);
  return (
    <Modal
      open
      title={discoveryText('discovery.removeEntryTitle', { name: section.name })}
      onCancel={onCancel}
      actions={[
        { kind: 'primary', label: t('discovery.removeTemplateOnly'), onAct: () => onRemove(empty ? 'Keep' : choice ?? 'Keep'), disabled: !empty && choice === null, disabledReason: t('discovery.refused.removeChoice') },
        { kind: 'cancel', label: t('modal.cancel'), onAct: onCancel },
      ]}
      dataModal="discovery-remove-section"
    >
      <div className={styles.saveBody}>
        <p className={styles.hint} data-discovery-remove-held={section.templateKey}>{sectionHeldLine(section)}</p>
        {empty ? null : (
          <RadioGroup
            name={`discovery-remove-${section.templateKey}`}
            legend={t('discovery.removeChoiceLegend')}
            options={[{ value: 'Keep', label: t('discovery.keepAnswersFilesDetail') }, { value: 'Remove', label: t('discovery.removeAnswersFilesDetail') }]}
            value={choice}
            onChange={(value) => setChoice(value === 'Remove' ? 'Remove' : 'Keep')}
          />
        )}
      </div>
    </Modal>
  );
}

/** 🔒 Save as Template (M4c): a name and a description, and the template joins the library with every section's questions and upload points; drawn through the Form frame (decision 3060). */
function SaveAsTemplateWindow({ card, apiBaseUrl, onClose, onSaved }: { card: DiscoveryCardAnswer; apiBaseUrl: string; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [refusal, setRefusal] = useState<string | null>(null);
  const save = async () => {
    const outcome = await post(dealDiscoverySaveAsTemplatePath(card.recordId), { name: name.trim(), description: description.trim() }, apiBaseUrl);
    if (!outcome.ok) return setRefusal(outcome.message);
    onSaved();
  };
  return (
    <FormFrame
      host="window"
      open
      title={t('discovery.saveAsTemplate')}
      dataForm="discovery-save-as-template"
      typed={name.trim() !== '' || description.trim() !== ''}
      onCancel={onClose}
      refusal={refusal}
      onDismissRefusal={() => setRefusal(null)}
      act={{ label: t('discovery.saveTemplate'), onAct: () => save(), disabled: name.trim() === '', disabledReason: t('discovery.nameNeeded') }}
    >
      <div className={styles.saveBody}>
        <p className={styles.hint}>{t('discovery.saveAsHint')}</p>
        <TextInput size={Size.Large} label={t('discovery.name')} value={name} onChange={setName} required name="discoveryTemplateName" />
        <Textarea size={Size.Large} label={t('discovery.description')} value={description} onChange={setDescription} rows={2} name="discoveryTemplateDescription" />
      </div>
    </FormFrame>
  );
}
